import { z } from "zod";
import { STAFF_CATALOG_BOUNDS, staffCatalogItemDetailSchema, staffCatalogItemStatuses, staffCatalogItemTypes, staffCatalogVersionSchema, } from "./staff-catalog.js";
// The contract of Places enrichment (CHT-709): the routes P1 to P7 under `/staff/catalog`. Same rules as
// `staff-catalog.ts`: every top-level shape is STRICT, the API validates bodies and queries with these
// schemas, and the admin and the typed client validate every response with them.
//
// STORAGE POLICY. Only the candidate list of P2 carries provider content (names, addresses, points,
// zones, types). It lives in one HTTP response (`Cache-Control: no-store`) and in the browser's memory
// while the search panel is open. No other shape here has a provider name, address, point or zone, and
// the admin must not copy a candidate value into a canonical field. The location form takes values a
// staff member typed from a source that is not the provider (`source` has no provider entry).
//
// This package cannot import the domain package. The tuples and bounds below repeat the domain's
// (`catalog-places.ts`); `apps/api` has a parity test that fails when one differs.
// --- codes (parity-tested against the domain) ------------------------------------------------------
export const staffCatalogPlaceProvider = "google_places";
export const staffCatalogPlaceMatchStates = [
    "CANDIDATES_FOUND",
    "NO_MATCH",
    "MATCHED",
    "NO_PLACE",
    "NEEDS_REVIEW",
    "UNMATCHED",
];
export const staffCatalogPlaceRefreshStates = ["FRESH", "STALE", "ERROR"];
export const staffCatalogPlaceMatchDecisions = [
    "SELECTED",
    "REPLACED",
    "CLEARED",
    "NO_PLACE",
];
export const staffCatalogPlaceReasonCodes = [
    "WRONG_MATCH",
    "PLACE_CLOSED",
    "PLACE_MOVED",
    "NOT_A_PLACE",
    "OTHER",
];
export const staffCatalogPlaceLookupKinds = ["SEARCH_TEXT", "GET_DETAILS"];
export const staffCatalogPlaceLookupPhases = ["STARTED", "FINISHED"];
export const staffCatalogPlaceFieldSets = ["SEARCH_CANDIDATES_V1", "VERIFY_ID_V1"];
export const staffCatalogPlaceSkuTiers = ["IDS_ONLY", "ESSENTIALS", "PRO"];
export const staffCatalogPlaceLookupOutcomes = ["OK", "NO_MATCH", "NOT_FOUND", "ERROR"];
export const staffCatalogPlacesErrorCodes = [
    "TIMEOUT",
    "RATE_LIMITED",
    "QUOTA_EXHAUSTED",
    "NOT_FOUND",
    "INVALID_REQUEST",
    "PERMISSION_DENIED",
    "UNAVAILABLE",
];
/** The sources a staff member may declare. The provider is NOT one of them. */
export const staffCatalogLocationSources = [
    "SITE_VISIT",
    "VENUE_PUBLISHED",
    "SUPPLIER_PROVIDED",
    "LICENSED_DATASET",
    "IMPORT_FILE",
    "NONE",
];
export const staffCatalogLocationStates = ["SET", "UNKNOWN"];
export const staffCatalogLocationAssessments = [
    "CONFIRMED",
    "UNCONFIRMED",
    "UNKNOWN",
    "NOT_ASSESSED",
];
export const staffCatalogLocationReviewFilters = ["NOT_ASSESSED", "UNCONFIRMED"];
export const staffCatalogPlaceReviewStates = [
    "CANDIDATES_FOUND",
    "NO_MATCH",
    "NEEDS_REVIEW",
    "UNMATCHED",
];
export const staffCatalogPlaceReviewRefreshStates = ["STALE", "ERROR"];
// --- bounds (parity-tested against the domain) -----------------------------------------------------
export const STAFF_CATALOG_PLACE_BOUNDS = Object.freeze({
    placeId: 255,
    searchText: 200,
    defaultCandidates: 5,
    maxCandidates: 10,
    radiusMinMeters: 100,
    radiusMaxMeters: 50_000,
    /** A search result can be chosen from for this long. */
    searchValidMinutes: 30,
    historyLimit: 20,
    reviewLimit: 100,
});
// --- primitives -------------------------------------------------------------------------------------
const id = z.string().min(1).max(64);
const count = z.number().int();
const nullableText = z.string().nullable();
const responseInstant = z.union([
    z.date(),
    z.iso.datetime({ offset: true }).transform((value) => new Date(value)),
]);
const nullableResponseInstant = responseInstant.nullable();
const COUNTRY = /^[A-Z]{2}$/;
const CURSOR = /^[A-Za-z0-9_-]+$/;
const placeId = z.string().min(1).max(STAFF_CATALOG_PLACE_BOUNDS.placeId);
const expectedVersion = staffCatalogVersionSchema;
const reasonText = z.string().min(1).max(STAFF_CATALOG_BOUNDS.reason);
// --- paths ------------------------------------------------------------------------------------------
/** What follows `/staff/catalog/items/:itemId/`. */
export const STAFF_CATALOG_PLACE_SUBPATHS = Object.freeze({
    place: "place",
    search: "place/search",
    select: "place/select",
    clear: "place/clear",
    verify: "place/verify",
    location: "location",
});
/** P7. The review queue is not under an item. */
export const STAFF_CATALOG_PLACE_REVIEW_PATH = "/staff/catalog/places/review";
/** The path of a place sub-resource of an item. The id is encoded, so it cannot add a path segment. */
export function staffCatalogPlacePath(itemId, subpath) {
    return `/staff/catalog/items/${encodeURIComponent(itemId)}/${subpath}`;
}
/** The same paths as Hono route templates (`:itemId`), for the API to register. */
export function staffCatalogPlaceRoute(subpath) {
    return `/staff/catalog/items/:itemId/${subpath}`;
}
// --- request bodies ---------------------------------------------------------------------------------
/**
 * P2. Both keys are optional. Without `text` the use case searches for the item's name and locality.
 * `near` biases the search to a circle; it is not a filter. The text is never stored (only its hash).
 * P2 is not a receipt command: its answer holds provider content, so a retry makes a new search that
 * counts against the budget.
 */
export const staffCatalogPlaceSearchBodySchema = z.strictObject({
    text: z.string().min(1).max(STAFF_CATALOG_PLACE_BOUNDS.searchText).optional(),
    near: z
        .strictObject({
        latitude: z.number().min(-90).max(90),
        longitude: z.number().min(-180).max(180),
        radiusMeters: z
            .number()
            .min(STAFF_CATALOG_PLACE_BOUNDS.radiusMinMeters)
            .max(STAFF_CATALOG_PLACE_BOUNDS.radiusMaxMeters),
    })
        .optional(),
});
/** A reason for replacing or clearing a match: a code, and free text for the audit trail. */
export const staffCatalogPlaceCorrectionSchema = z.strictObject({
    reasonCode: z.enum(staffCatalogPlaceReasonCodes),
    reason: reasonText,
});
/**
 * P3. `searchLookupId` and `placeId` must come from one P2 answer of this item, less than
 * `searchValidMinutes` old. An item that already has a match needs `correction` (a replace).
 */
export const staffCatalogPlaceSelectBodySchema = z.strictObject({
    expectedVersion,
    searchLookupId: id,
    placeId,
    correction: staffCatalogPlaceCorrectionSchema.optional(),
});
/**
 * P4. Removes the match. `noPlace: true` also says the item has no physical place (state `NO_PLACE`);
 * without it the state is `UNMATCHED`.
 */
export const staffCatalogPlaceClearBodySchema = z.strictObject({
    expectedVersion,
    reasonCode: z.enum(staffCatalogPlaceReasonCodes),
    reason: reasonText,
    noPlace: z.boolean().optional(),
});
/** P5. Asks for a verification run of the stored place id. Needs no version: it changes no item column. */
export const staffCatalogPlaceVerifyBodySchema = z.strictObject({});
const UNKNOWN = z.literal("UNKNOWN");
/**
 * P6. Every group says a value or `"UNKNOWN"`: nothing is "left as it was", so a confirmation always
 * describes the whole location. A value comes from `source`, which is never the provider. `NONE` is for
 * a confirmation where every group is `UNKNOWN`; any other source needs at least one value.
 * Coordinates are rounded to 6 decimals by the domain. The time zone must be an IANA name.
 */
export const staffCatalogLocationConfirmBodySchema = z
    .strictObject({
    expectedVersion,
    point: z.union([
        UNKNOWN,
        z.strictObject({
            latitude: z.number().min(-90).max(90),
            longitude: z.number().min(-180).max(180),
        }),
    ]),
    place: z.union([
        UNKNOWN,
        z.strictObject({
            locality: z.string().min(1).max(STAFF_CATALOG_BOUNDS.locality),
            region: z.string().min(1).max(STAFF_CATALOG_BOUNDS.region).nullish(),
            countryCode: z.string().regex(COUNTRY),
            addressText: z.string().min(1).max(STAFF_CATALOG_BOUNDS.addressText).nullish(),
        }),
    ]),
    timeZone: z.union([UNKNOWN, z.string().min(1).max(64)]),
    source: z.enum(staffCatalogLocationSources),
})
    .refine((body) => {
    const anyValue = body.point !== "UNKNOWN" || body.place !== "UNKNOWN" || body.timeZone !== "UNKNOWN";
    return body.source === "NONE" ? !anyValue : anyValue;
}, { message: "the source NONE needs every group UNKNOWN, and any other source needs a value" });
const limitQuery = z
    .string()
    .regex(/^\d{1,3}$/)
    .transform(Number)
    .pipe(z.number().int().min(1).max(STAFF_CATALOG_PLACE_BOUNDS.reviewLimit));
/**
 * P7. `state` lists the match states that need a person; `refresh` lists failing or old verifications;
 * `location` lists items whose geography nobody confirmed. Filters combine with AND. Keyset page: pass
 * `nextCursor` back as `cursor`.
 */
export const staffCatalogPlaceReviewQuerySchema = z.strictObject({
    state: z.enum(staffCatalogPlaceReviewStates).optional(),
    refresh: z.enum(staffCatalogPlaceReviewRefreshStates).optional(),
    location: z.enum(staffCatalogLocationReviewFilters).optional(),
    type: z.enum(staffCatalogItemTypes).optional(),
    countryCode: z.string().regex(COUNTRY).optional(),
    limit: limitQuery.optional(),
    cursor: z.string().min(1).max(512).regex(CURSOR).optional(),
});
// --- responses: place view (P1, P3, P4) ----------------------------------------------------------------
/**
 * Where the item is in the matching flow. `matchState` and `refreshState` are `null` when the item
 * never entered the flow. `refreshState` is derived: a matched item whose last verification is older
 * than 400 days is `STALE` even before a job ran.
 */
export const staffCatalogPlaceStateSchema = z.strictObject({
    matchState: z.enum(staffCatalogPlaceMatchStates).nullable(),
    refreshState: z.enum(staffCatalogPlaceRefreshStates).nullable(),
    lastVerifiedAt: nullableResponseInstant,
    nextVerifyAt: nullableResponseInstant,
    consecutiveFailures: count,
    lastErrorCode: z.enum(staffCatalogPlacesErrorCodes).nullable(),
});
/**
 * The place id of the item (the one `catalog_external_refs` row with provider `google_places`).
 * `verified` is false for an id that came from a plain external-ref edit or an import and that no
 * select or verification run has confirmed: the UI says "Matched (unverified)".
 */
export const staffCatalogPlaceCurrentSchema = z.strictObject({
    provider: z.literal(staffCatalogPlaceProvider),
    placeId,
    observedAt: responseInstant,
    verified: z.boolean(),
});
/** One decision of the match history, newest first. It holds ids and codes, never a place name. */
export const staffCatalogPlaceMatchSchema = z.strictObject({
    id,
    decision: z.enum(staffCatalogPlaceMatchDecisions),
    placeId: placeId.nullable(),
    previousPlaceId: placeId.nullable(),
    candidateRank: count.nullable(),
    reasonCode: z.enum(staffCatalogPlaceReasonCodes).nullable(),
    itemVersion: count,
    decidedByStaffUserId: id,
    createdAt: responseInstant,
});
/** One provider call, as a result row (a `STARTED` row without a result is `phase: STARTED`). */
export const staffCatalogPlaceLookupSchema = z.strictObject({
    id,
    lookupKind: z.enum(staffCatalogPlaceLookupKinds),
    phase: z.enum(staffCatalogPlaceLookupPhases),
    fieldSet: z.enum(staffCatalogPlaceFieldSets),
    skuTier: z.enum(staffCatalogPlaceSkuTiers),
    outcome: z.enum(staffCatalogPlaceLookupOutcomes).nullable(),
    errorCode: z.enum(staffCatalogPlacesErrorCodes).nullable(),
    candidateCount: count.nullable(),
    latencyMs: count.nullable(),
    actorKind: z.string(),
    actorId: id.nullable(),
    createdAt: responseInstant,
});
/** What staff see about the canonical geography of the item: one assessment per group. */
export const staffCatalogLocationStatusSchema = z.strictObject({
    point: z.enum(staffCatalogLocationAssessments),
    place: z.enum(staffCatalogLocationAssessments),
    timeZone: z.enum(staffCatalogLocationAssessments),
    /** The source of the current confirmation. `null` when there is none, or a later edit made it not current. */
    source: z.enum(staffCatalogLocationSources).nullable(),
    confirmedAt: nullableResponseInstant,
    confirmedByStaffUserId: id.nullable(),
    /** From Lafayette's own city table (never from the provider). A hint for the zone field only. */
    suggestedTimeZone: nullableText,
});
/**
 * P1. `itemVersion` is the token to send as `expectedVersion`. `lookupAvailable` is false when the
 * server runs with `LAFAYETTE_PLACES=off`: P2, P3 and P5 then answer 503 and the UI says "Place lookup
 * is not configured". The view has NO provider name, address, point or zone.
 */
export const staffCatalogPlaceViewSchema = z.strictObject({
    itemId: id,
    itemVersion: count,
    itemStatus: z.enum(staffCatalogItemStatuses),
    lookupAvailable: z.boolean(),
    state: staffCatalogPlaceStateSchema,
    current: staffCatalogPlaceCurrentSchema.nullable(),
    matches: z.array(staffCatalogPlaceMatchSchema).max(STAFF_CATALOG_PLACE_BOUNDS.historyLimit),
    lookups: z.array(staffCatalogPlaceLookupSchema).max(STAFF_CATALOG_PLACE_BOUNDS.historyLimit),
    location: staffCatalogLocationStatusSchema,
});
// --- responses: search (P2) -----------------------------------------------------------------------------
/**
 * One result of a search. TRANSIENT provider content, shown to staff next to the attribution and then
 * dropped. `rank` is the position in the list (1 first); P3 stores the rank with the decision.
 */
export const staffCatalogPlaceCandidateSchema = z.strictObject({
    rank: z.number().int().min(1).max(STAFF_CATALOG_PLACE_BOUNDS.maxCandidates),
    placeId,
    displayName: nullableText,
    formattedAddress: nullableText,
    point: z.strictObject({ latitude: z.number(), longitude: z.number() }).nullable(),
    timeZone: nullableText,
    typeCodes: z.array(z.string()),
    closedPermanently: z.boolean().nullable(),
    mapsUrl: nullableText,
    attributions: z.array(z.strictObject({ provider: z.string(), uri: nullableText })),
});
/**
 * P2. `searchLookupId` goes back to P3. The answer holds provider content, so the response carries
 * `Cache-Control: no-store`. `state` is the item's state after the search (`CANDIDATES_FOUND` for two
 * or more candidates, `NO_MATCH` for none, unchanged for an item that is `MATCHED`). `attributionRequired`
 * is always true: show "Google Maps" next to the list and every provider named in a candidate.
 */
export const staffCatalogPlaceSearchResultSchema = z.strictObject({
    searchLookupId: id,
    searchedAt: responseInstant,
    expiresAt: responseInstant,
    state: z.enum(staffCatalogPlaceMatchStates),
    candidates: z
        .array(staffCatalogPlaceCandidateSchema)
        .max(STAFF_CATALOG_PLACE_BOUNDS.maxCandidates),
    attributionRequired: z.literal(true),
});
// --- responses: verification (P5), location (P6) --------------------------------------------------------
/** P5. The run happens later. Read P1 to see its result. A retry with the same key answers the same intent. */
export const staffCatalogPlaceVerificationQueuedSchema = z.strictObject({
    intentId: id,
    queued: z.literal(true),
});
/** P6. The item's new detail (the version, the geography and the readiness) and the new place view. */
export const staffCatalogLocationResultSchema = z.strictObject({
    detail: staffCatalogItemDetailSchema,
    place: staffCatalogPlaceViewSchema,
});
// --- responses: review queue (P7) -----------------------------------------------------------------------
export const staffCatalogPlaceReviewRowSchema = z.strictObject({
    itemId: id,
    itemType: z.enum(staffCatalogItemTypes),
    name: z.string(),
    slug: z.string(),
    status: z.enum(staffCatalogItemStatuses),
    locality: nullableText,
    countryCode: nullableText,
    matchState: z.enum(staffCatalogPlaceMatchStates).nullable(),
    refreshState: z.enum(staffCatalogPlaceRefreshStates).nullable(),
    lastVerifiedAt: nullableResponseInstant,
    lastErrorCode: z.enum(staffCatalogPlacesErrorCodes).nullable(),
    /** The assessment of the point group, the one that decides whether the item can be found by distance. */
    locationPoint: z.enum(staffCatalogLocationAssessments),
    updatedAt: responseInstant,
});
export const staffCatalogPlaceReviewPageSchema = z.strictObject({
    items: z.array(staffCatalogPlaceReviewRowSchema),
    nextCursor: z.string().min(1).max(512).nullable(),
});
